import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Loader2 } from "lucide-react";

// Modal de ajuste da foto de perfil, no estilo do WhatsApp: a foto aparece
// inteira escurecida e só a área dentro do círculo (o que vai virar o avatar)
// fica clara. Arrasta pra reposicionar, slider pra dar zoom, e o recorte é
// feito num canvas 256x256 antes de subir — então o servidor já recebe a
// imagem final e pequena (o limite do /me/avatar é apertado de propósito).
const VIEW = 300; // lado da área de edição (px)
const D = 260;    // diâmetro do círculo = lado do recorte (px)
const OUT = 256;  // tamanho final do avatar salvo (px)

export default function AvatarCropModal({ file, onCancel, onSave, saving }) {
  const [img, setImg] = useState(null);
  const [erro, setErro] = useState("");
  const [zoom, setZoom] = useState(1);
  const [off, setOff] = useState({ x: 0, y: 0 });
  const drag = useRef(null);

  // Carrega a imagem escolhida
  useEffect(() => {
    const url = URL.createObjectURL(file);
    const i = new Image();
    i.onload = () => setImg(i);
    i.onerror = () => setErro("Não consegui ler essa imagem — tenta outro arquivo.");
    i.src = url;
    return () => URL.revokeObjectURL(url);
  }, [file]);

  // Escala base = "cover" no círculo (a imagem sempre preenche o recorte)
  const baseScale = useMemo(() => (img ? Math.max(D / img.naturalWidth, D / img.naturalHeight) : 1), [img]);
  const scale = baseScale * zoom;
  const dw = img ? img.naturalWidth * scale : 0;
  const dh = img ? img.naturalHeight * scale : 0;

  // Mantém o recorte sempre coberto pela imagem (sem "buraco" dentro do círculo)
  const clamp = (o, w = dw, h = dh) => {
    const maxX = Math.max(0, (w - D) / 2);
    const maxY = Math.max(0, (h - D) / 2);
    return { x: Math.min(maxX, Math.max(-maxX, o.x)), y: Math.min(maxY, Math.max(-maxY, o.y)) };
  };

  const onZoom = (z) => {
    setZoom(z);
    if (img) {
      const s = baseScale * z;
      setOff((o) => clamp(o, img.naturalWidth * s, img.naturalHeight * s));
    }
  };

  const onPointerDown = (e) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = { x: e.clientX, y: e.clientY, ox: off.x, oy: off.y };
  };
  const onPointerMove = (e) => {
    if (!drag.current) return;
    setOff(clamp({ x: drag.current.ox + (e.clientX - drag.current.x), y: drag.current.oy + (e.clientY - drag.current.y) }));
  };
  const onPointerUp = () => { drag.current = null; };

  const salvar = () => {
    if (!img) return;
    const left = VIEW / 2 - dw / 2 + off.x;
    const top = VIEW / 2 - dh / 2 + off.y;
    const cropLeft = VIEW / 2 - D / 2;
    const cropTop = VIEW / 2 - D / 2;
    const canvas = document.createElement("canvas");
    canvas.width = OUT; canvas.height = OUT;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#ffffff"; // PNG com transparência não vira preto no JPEG
    ctx.fillRect(0, 0, OUT, OUT);
    ctx.drawImage(img, (cropLeft - left) / scale, (cropTop - top) / scale, D / scale, D / scale, 0, 0, OUT, OUT);
    onSave(canvas.toDataURL("image/jpeg", 0.9));
  };

  return createPortal(
    <div
      onClick={() => !saving && onCancel()}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-zinc-900 border border-zinc-700 rounded-xl text-zinc-100"
        style={{ width: "100%", maxWidth: 360, padding: 20 }}
      >
        <div className="font-display font-bold text-base">Ajustar foto de perfil</div>
        <div className="text-xs text-zinc-400 mt-1 mb-4">Arraste pra posicionar e use o zoom. A parte dentro do círculo é a que vai aparecer.</div>

        {erro ? (
          <div className="text-sm text-red-400 py-10 text-center">{erro}</div>
        ) : !img ? (
          <div className="flex justify-center py-16"><Loader2 className="h-5 w-5 animate-spin text-zinc-400" /></div>
        ) : (
          <>
            <div
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              onPointerCancel={onPointerUp}
              style={{ position: "relative", width: VIEW, height: VIEW, maxWidth: "100%", margin: "0 auto", overflow: "hidden", borderRadius: 12, background: "#000", cursor: "grab", touchAction: "none", userSelect: "none" }}
            >
              <img
                src={img.src}
                alt=""
                draggable={false}
                style={{ position: "absolute", width: dw, height: dh, left: VIEW / 2 - dw / 2 + off.x, top: VIEW / 2 - dh / 2 + off.y, maxWidth: "none", pointerEvents: "none" }}
              />
              {/* Círculo: o "buraco" claro no meio, com todo o resto escurecido */}
              <div
                style={{ position: "absolute", left: (VIEW - D) / 2, top: (VIEW - D) / 2, width: D, height: D, borderRadius: "50%", boxShadow: "0 0 0 600px rgba(0,0,0,0.62)", border: "2px solid rgba(255,255,255,0.9)", pointerEvents: "none" }}
              />
            </div>

            <div className="flex items-center gap-3 mt-4 px-1">
              <span className="text-xs text-zinc-500">−</span>
              <input
                type="range" min="1" max="3" step="0.01" value={zoom}
                onChange={(e) => onZoom(parseFloat(e.target.value))}
                className="flex-1 accent-blue-500"
                aria-label="Zoom"
              />
              <span className="text-xs text-zinc-500">+</span>
            </div>
          </>
        )}

        <div className="flex justify-end gap-2 mt-5">
          <button
            onClick={onCancel}
            disabled={saving}
            className="px-4 py-2 rounded-md text-sm text-zinc-300 hover:text-white hover:bg-zinc-800 transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={salvar}
            disabled={!img || saving}
            className="px-4 py-2 rounded-md text-sm font-medium bg-blue-600 hover:bg-blue-500 text-white transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            Salvar foto
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
